import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingCart, Star, Filter, Eye, Plus, Minus, AlertTriangle, Wifi } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import ProductDetailModal from '../components/ProductDetailModal';
import ScrollToTop from '../components/ScrollToTop';
import { getStockUrgencyBadge } from '../utils/stockUtils';
import { trackAddToCart } from '../utils/fbpixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [productQuantities, setProductQuantities] = useState({});
  const [cartCount, setCartCount] = useState(0);

  // Function to update cart count from localStorage
  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const count = cart.reduce((total, item) => total + item.quantity, 0);
    setCartCount(count);
  };

  useEffect(() => {
    fetchProducts();
    updateCartCount(); // Initialize cart count on mount
  }, []);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === selectedCategory));
    }
    // Scroll to top when category changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedCategory, products]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/`);
      setProducts(response.data);
      setFilteredProducts(response.data);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(response.data.map(p => p.category))];
      setCategories(uniqueCategories);
      
      // Initialize quantities to 1 for each product
      const quantities = {};
      response.data.forEach(p => quantities[p.id] = 1);
      setProductQuantities(quantities);
    } catch (error) {
      toast.error('Failed to load products');
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (productId, delta) => {
    setProductQuantities(prev => ({
      ...prev,
      [productId]: Math.max(1, (prev[productId] || 1) + delta)
    }));
  };

  const addToCart = (product, customQuantity = null, customUrl = '') => {
    const quantity = customQuantity || productQuantities[product.id] || 1;
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find(item => item.id === product.id && item.custom_url === customUrl);

    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({ ...product, quantity, custom_url: customUrl });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    updateCartCount();
    // Meta Pixel — AddToCart (fires AFTER cart is successfully updated)
    trackAddToCart({
      id: product.id,
      name: product.title,
      price: product.sale_price || product.price,
      quantity,
    });
    toast.success(`Added ${quantity} ${quantity > 1 ? 'items' : 'item'} to cart!`);
  };

  const getRelatedProducts = (product) => {
    return products
      .filter(p => p.id !== product.id && p.category === product.category)
      .slice(0, 3);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation - Same as Homepage */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="text-2xl font-bold text-white flex items-center gap-2 hover:text-blue-400 transition-colors"
            >
              <Wifi className="w-7 h-7 text-blue-500" />
              ShopNFCTags
            </button>
            
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/products')} className="text-white font-semibold">
                Shop
              </button>
              <a href="/#how-it-works" className="text-gray-300 hover:text-white transition-colors">
                How It Works
              </a>
              <a href="/#use-cases" className="text-gray-300 hover:text-white transition-colors">
                Use Cases
              </a>
              <button onClick={() => navigate('/bulk-orders')} className="text-gray-300 hover:text-white transition-colors">
                Bulk Orders
              </button>
              <a href="/#custom" className="text-gray-300 hover:text-white transition-colors">
                Custom
              </a>
              <button onClick={() => navigate('/contact')} className="text-gray-300 hover:text-white transition-colors">
                Contact
              </button>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/cart')}
                className="relative p-2 text-gray-300 hover:text-white transition-colors"
                aria-label="Shopping cart"
              >
                <ShoppingCart className="w-6 h-6" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
              <Button 
                onClick={() => navigate('/products')}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                Shop Now
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Smart NFC Products
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Browse our collection of premium NFC keychains, signs, and business cards
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 px-6 bg-gray-900 border-b border-gray-800 sticky top-[72px] z-40">
        <div className="container mx-auto">
          <div className="flex items-center gap-4 overflow-x-auto">
            <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-2 rounded-full font-medium transition-colors flex-shrink-0 ${
                selectedCategory === 'all'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              All Products
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-colors flex-shrink-0 ${
                  selectedCategory === category
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 px-6">
        <div className="container mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">Loading products...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No products found in this category</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredProducts.map((product) => {
                const quantity = productQuantities[product.id] || 1;
                const stockBadge = getStockUrgencyBadge(product.inventory_quantity);
                
                return (
                  <Card 
                    key={product.id}
                    className="bg-gray-900 border-gray-800 hover:border-blue-500 transition-all hover:shadow-xl hover:shadow-blue-500/20 group relative"
                  >
                    <CardContent className="p-0">
                      {/* Product Image */}
                      <div className="relative overflow-hidden bg-white aspect-square">
                        <img
                          src={
                            product.images[0]
                              ? product.images[0].startsWith('http')
                                ? product.images[0]
                                : `${API_URL}${product.images[0]}`
                              : 'https://via.placeholder.com/400'
                          }
                          alt={product.title}
                          className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-300"
                        />
                        
                        {/* Quick View Button (appears on hover) */}
                        <button
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowDetailModal(true);
                          }}
                          className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <div className="bg-white text-black px-4 py-2 rounded-full font-semibold flex items-center gap-2">
                            <Eye className="w-5 h-5" />
                            Quick View
                          </div>
                        </button>
                        
                        {/* Urgency Badge - Top Left */}
                        {stockBadge.show && (
                          <div className={`absolute top-4 left-4 ${stockBadge.className} px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1 shadow-lg`}>
                            {stockBadge.urgent && <AlertTriangle className="w-4 h-4" />}
                            {stockBadge.text}
                          </div>
                        )}
                        
                        {product.is_featured && (
                          <div className="absolute top-4 right-4 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                            Featured
                          </div>
                        )}
                        {product.sale_price && !stockBadge.show && (
                          <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                            Sale
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="p-3">
                        <div className="mb-1">
                          <span className="text-xs text-gray-400 uppercase tracking-wide">
                            {product.category}
                          </span>
                        </div>
                        
                        <h3 
                          onClick={() => {
                            setSelectedProduct(product);
                            setShowDetailModal(true);
                          }}
                          className="text-base font-bold text-white mb-1 group-hover:text-blue-400 transition-colors cursor-pointer line-clamp-1"
                        >
                          {product.title}
                        </h3>
                        
                        <p className="text-gray-400 text-xs mb-2 line-clamp-1">
                          {product.short_description}
                        </p>

                        {/* Rating */}
                        <div className="flex items-center gap-1 mb-2">
                          <div className="flex">
                            {renderStars(4)}
                          </div>
                          <span className="text-gray-500 text-xs">(4.0)</span>
                        </div>

                        {/* Price */}
                        <div className="mb-2">
                          {product.sale_price ? (
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-white">
                                ${product.sale_price}
                              </span>
                              <span className="text-xs text-gray-500 line-through">
                                ${product.price}
                              </span>
                            </div>
                          ) : (
                            <span className="text-lg font-bold text-white">
                              ${product.price}
                            </span>
                          )}
                        </div>

                        {/* Stock Status */}
                        <div className="mb-2">
                          <span className={`text-xs font-medium ${
                            product.stock_status === 'in_stock' 
                              ? 'text-green-400' 
                              : product.stock_status === 'pre_order'
                              ? 'text-yellow-400'
                              : 'text-red-400'
                          }`}>
                            {product.stock_status === 'in_stock' && '✓ In Stock'}
                            {product.stock_status === 'out_of_stock' && '✗ Out of Stock'}
                            {product.stock_status === 'pre_order' && '⏳ Pre-Order'}
                          </span>
                        </div>

                        {/* Quantity Selector - Compact */}
                        <div className="mb-2">
                          <label className="block text-xs font-medium text-gray-400 mb-1">Qty:</label>
                          <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1 w-fit">
                            <button
                              onClick={() => updateQuantity(product.id, -1)}
                              className="p-1 hover:bg-gray-700 rounded transition-colors"
                            >
                              <Minus className="w-3 h-3 text-gray-300" />
                            </button>
                            <span className="text-white font-medium w-6 text-center text-sm">{quantity}</span>
                            <button
                              onClick={() => updateQuantity(product.id, 1)}
                              className="p-1 hover:bg-gray-700 rounded transition-colors"
                            >
                              <Plus className="w-3 h-3 text-gray-300" />
                            </button>
                          </div>
                        </div>

                        {/* Add to Cart Button */}
                        <Button
                          onClick={() => addToCart(product)}
                          disabled={product.stock_status === 'out_of_stock'}
                          className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 text-sm"
                        >
                          <ShoppingCart className="w-4 h-4 mr-1" />
                          Add to Cart
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need Custom Solutions?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Get in touch for bulk orders, custom designs, and special pricing
          </p>
          <Button
            onClick={() => navigate('/customize')}
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
          >
            Design Your Custom Solution
          </Button>
        </div>
      </section>

      {/* Footer - Same as Homepage */}
      <footer className="bg-gray-950 border-t border-gray-800 py-12">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <h3 className="text-xl font-bold text-white mb-4">ShopNFCTags</h3>
              <p className="text-gray-400 text-sm mb-4">
                Premium NFC products for modern professionals. Share your contact instantly with a tap.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/products')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Shop
                  </button>
                </li>
                <li>
                  <a href="/#how-it-works" className="text-gray-400 hover:text-white transition-colors text-sm">
                    How It Works
                  </a>
                </li>
                <li>
                  <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            {/* Products */}
            <div>
              <h4 className="text-white font-semibold mb-4">Products</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => { setSelectedCategory('KEYCHAINS'); window.scrollTo(0, 800); }} className="text-gray-400 hover:text-white transition-colors text-sm">
                    NFC Keychains
                  </button>
                </li>
                <li>
                  <button onClick={() => { setSelectedCategory('BUSINESS CARDS'); window.scrollTo(0, 800); }} className="text-gray-400 hover:text-white transition-colors text-sm">
                    NFC Business Cards
                  </button>
                </li>
                <li>
                  <button onClick={() => { setSelectedCategory('SIGNS'); window.scrollTo(0, 800); }} className="text-gray-400 hover:text-white transition-colors text-sm">
                    NFC Signs
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Bulk Orders
                  </button>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/privacy-policy')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms-of-service')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/refunds-returns')} className="text-gray-400 hover:text-white transition-colors text-sm">
                    Refunds & Returns
                  </button>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-500 text-sm">© 2026 ShopNFCTags. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setTimeout(() => setSelectedProduct(null), 300);
          }}
          onAddToCart={addToCart}
        />
      )}
      <ScrollToTop />
    </div>
  );
};

export default Products;
